import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import {
  getUsersWithRoles,
  addUserWithRoleToFirestore,
  updateUserRoleInFirestore,
  removeUserFromFirestore,
} from '../utils/firebaseAuth';
import { ROLES, ROLE_LABELS } from '../config/roles';
import { Users, Crown, UserCircle } from 'lucide-react';
import { toastManager } from '../utils/toastManager';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState(ROLES.CONSULTANT);
  const [editedRoles, setEditedRoles] = useState({});
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState(null);

  const currentUserEmail = auth.currentUser?.email?.toLowerCase();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const list = await getUsersWithRoles();
      setUsers(list);
    } catch (err) {
      console.error('Error fetching users:', err);
      const friendlyMsg = (err.message || '').toLowerCase().includes('offline')
        ? 'Unable to connect. Please check your internet connection and try again.'
        : 'Failed to fetch users';
      setError(friendlyMsg);
      toastManager.error(friendlyMsg);
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email?.trim() || '');

  const addUser = async (e) => {
    e.preventDefault();
    const email = newUserEmail?.trim();
    if (!email) {
      setError('Please enter an email address');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await addUserWithRoleToFirestore(email, newUserRole);
      const exists = users.some((u) => u.email.toLowerCase() === email.toLowerCase());
      toastManager.success(exists ? 'Role updated successfully' : 'User added successfully');
      setNewUserEmail('');
      await fetchUsers();
    } catch (err) {
      console.error('Error adding user:', err);
      const msg = err.message || 'Failed to add user';
      const friendlyMsg = msg.toLowerCase().includes('offline')
        ? 'Unable to connect. Please check your internet connection and try again.'
        : msg;
      setError(friendlyMsg);
      toastManager.error(friendlyMsg);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (email, newRole) => {
    try {
      setLoading(true);
      setError('');
      await updateUserRoleInFirestore(email, newRole);
      toastManager.success('Role updated');
      await fetchUsers();
    } catch (err) {
      console.error('Error updating role:', err);
      const msg = err.message || 'Failed to update role';
      const friendlyMsg = msg.toLowerCase().includes('offline')
        ? 'Unable to connect. Please check your internet connection and try again.'
        : msg;
      setError(friendlyMsg);
      toastManager.error(friendlyMsg);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (email) => {
    setDeleteConfirmEmail(null);
    if (email?.toLowerCase() === currentUserEmail) {
      toastManager.error('You cannot remove yourself');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await removeUserFromFirestore(email);
      toastManager.success('User removed');
      await fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      const msg = err.message || 'Failed to remove user';
      const friendlyMsg = msg.toLowerCase().includes('offline')
        ? 'Unable to connect. Please check your internet connection and try again.'
        : msg;
      setError(friendlyMsg);
      toastManager.error(friendlyMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const roleOptions = [
    { value: ROLES.CONSULTANT, label: ROLE_LABELS[ROLES.CONSULTANT] },
    { value: ROLES.SUPPORT, label: ROLE_LABELS[ROLES.SUPPORT] },
    { value: ROLES.ADMIN, label: ROLE_LABELS[ROLES.ADMIN] },
  ];

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <h1>User Management</h1>
        <p>Manage platform users and roles (stored in Firestore)</p>
      </div>

      <div className="add-user-section">
        <div className="card">
          <h3>Add User</h3>
          <p className="form-hint">Enter email and select role. Users must sign in with Google using the same email.</p>
          <form onSubmit={addUser} className="add-user-form">
            <div className="form-group">
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="Enter user email address"
                className="form-input"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value)}
                className="form-select"
                disabled={loading}
              >
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !newUserEmail?.trim()}
            >
              {loading ? 'Adding...' : 'Add User'}
            </button>
          </form>
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>

      <div className="users-list-section">
        <div className="card">
          <div className="card-header">
            <h3>Users ({users.length})</h3>
            <button
              onClick={fetchUsers}
              className="btn-secondary"
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="loading-spinner"></div>
          ) : (
            <div className="users-table">
              <div className="table-header">
                <div className="table-cell">User</div>
                <div className="table-cell">Email</div>
                <div className="table-cell">Role</div>
                <div className="table-cell">Actions</div>
              </div>

              {users.map((user) => (
                <div key={user.id} className="table-row">
                  <div className="table-cell">
                    <div className="user-info">
                      <div className="user-avatar">
                        {(user.displayName || user.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="user-details">
                        <span className="user-name">{user.displayName}</span>
                        <span className="user-date">Joined via Firestore</span>
                      </div>
                    </div>
                  </div>

                  <div className="table-cell">
                    <span className="user-email">{user.email}</span>
                  </div>

                  <div className="table-cell">
                    {user.email.toLowerCase() === currentUserEmail ? (
                      <span className="role-readonly" title="You cannot change your own role">
                        {ROLE_LABELS[user.role]} <span className="you-badge">(you)</span>
                      </span>
                    ) : (
                      <select
                        value={editedRoles[user.email] ?? user.role}
                        onChange={(e) =>
                          setEditedRoles((prev) => ({ ...prev, [user.email]: e.target.value }))
                        }
                        className={`role-select ${editedRoles[user.email] ?? user.role}`}
                        disabled={loading}
                      >
                        {roleOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="table-cell">
                    <div className="action-buttons">
                      {user.email.toLowerCase() !== currentUserEmail && (
                        <button
                          className="btn-update"
                          onClick={() =>
                            updateUserRole(user.email, editedRoles[user.email] ?? user.role)
                          }
                          disabled={
                            loading ||
                            (editedRoles[user.email] ?? user.role) === user.role
                          }
                          title="Update role"
                        >
                          Update
                        </button>
                      )}
                      <button
                        className="btn-delete"
                        onClick={() => setDeleteConfirmEmail(user.email)}
                        disabled={loading || user.email.toLowerCase() === currentUserEmail}
                        title={
                          user.email.toLowerCase() === currentUserEmail
                            ? 'You cannot remove yourself'
                            : 'Remove user'
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <Users className="stat-icon" size={24} />
            <div className="stat-info">
              <h4>Total Users</h4>
              <span className="stat-value">{users.length}</span>
            </div>
          </div>
          <div className="stat-card">
            <Crown className="stat-icon" size={24} />
            <div className="stat-info">
              <h4>Admins</h4>
              <span className="stat-value">
                {users.filter((u) => u.role === ROLES.ADMIN).length}
              </span>
            </div>
          </div>
          <div className="stat-card">
            <UserCircle className="stat-icon" size={24} />
            <div className="stat-info">
              <h4>Consultants</h4>
              <span className="stat-value">
                {users.filter((u) => u.role === ROLES.CONSULTANT).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="signout-section">
        <button onClick={handleSignOut} className="btn-signout">
          Sign Out
        </button>
      </div>

      {deleteConfirmEmail && (
        <div
          className="confirm-overlay"
          onClick={() => setDeleteConfirmEmail(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-user-title"
        >
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="delete-user-title">Remove user?</h3>
            <p>
              Remove {deleteConfirmEmail} from the platform? They will no longer be able to access the app.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn cancel-btn"
                onClick={() => setDeleteConfirmEmail(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn confirm-primary"
                onClick={() => deleteUser(deleteConfirmEmail)}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
